<script lang="ts">
  import { Chip, ChipDeprecated, type FacetValue } from "@otto-ec/heureka-components/facet-value";
  import { ChipAction, type NextBestFilter } from "@otto-ec/heureka-components/filter";
  import { type FacetValueLabelOptions, Text } from "@otto-ec/heureka-components/facet-value-label";
  import DeselectAll from "../facetvaluetags/DeselectAll.svelte";
  import type { DeselectAllConfiguration } from "./type";
  import { OverflowBox } from "@otto-ec/heureka-components/facet-value-group";
  import { removeStatus } from "@otto-ec/heureka-components/tracking";
  import { isNba, isNotNba } from "../filter/SvelteFacetValue";
  import { heureka } from "@otto-ec/heureka";
  import {
    getOrSetInitialTopQuickFacet,
    getOrSetInitialNbaVisibleValues,
    setInitialNbaChipIds,
    getInitialNbaChipIds,
  } from "./topQuickFacetStore";
  import { getActiveVariation } from "../experiment/Experiments";
  import { dereference, readLabel, updateLabel, updateStatus } from "../tracking/FeatureTracking";
  import { setChangeLabels } from "../filter/FilterTracking";
  import { getElementById } from "../util/Utils";
  import { emptyCategoryPathStore, selectedCategoryPathValues } from "../categorypath/categoryPathStore";
  import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";
  import { eventQBus } from "../types/EventQBus";
  import { isActive, isNotActive } from "../toggle/Toggle";

  const defaultFacetValueLabelOptions: Partial<FacetValueLabelOptions> = {
    marginLeft: false,
    size: "75",
    showCount: false,
  };

  interface Props {
    nbaTitles?: string[];
    deselectAll: DeselectAllConfiguration;
    containsOnlyNbaValues?: boolean;
    values?: FacetValue[];
    nextBestFilters?: NextBestFilter[];
    hideDeselectAllOnDesktopView?: boolean;
    showNextBestFiltersFirst?: boolean;
  }

  const {
    nbaTitles,
    deselectAll,
    containsOnlyNbaValues,
    values = [],
    nextBestFilters = [],
    hideDeselectAllOnDesktopView,
    showNextBestFiltersFirst,
  }: Props = $props();
  /*                                              */
  const uniqueNbaTitles = $derived(nbaTitles ? Array.from(new Set(nbaTitles)) : undefined);
  const topQuickFacet = $derived(uniqueNbaTitles?.join("|") || "none");
  const hasValues = $derived(values?.length > 0);
  /*                                                                             */
  const nbaValues: FacetValue[] = $derived(values?.map(removeStatus) ?? []);
  const hasNextBestFilters = $derived(nextBestFilters.length > 0);

  /*                 */
  /*                                                                                             */
  /*                                                                                           */
  /*                                                                                  */
  const containsCategoryPath = $derived(!!values.find((value) => value.name === "kategorien"));
  const initialSelectedValues = $derived(nbaValues.filter((value) => value.checked));
  const initialSelectedIds = $derived(initialSelectedValues.map((value) => value.referencedFacetValueId));
  const selectedCategories: FacetValue[] = $derived(
    Array.from(containsCategoryPath ? $selectedCategoryPathValues : [])
      .filter((value) => !initialSelectedIds.includes(value.id))
      .map((value) => {
        return {
          id: value.id,
          checked: value.checked,
          title: value.title,
          referencedFacetValueId: value.ref || value.id,
        };
      }),
  );
  const selectedWithCategories = $derived(initialSelectedValues.concat(selectedCategories));
  const selectedIds = $derived(selectedWithCategories.map((value) => value.referencedFacetValueId));
  const unselectedWithoutSelectedCategories = $derived(
    nbaValues
      .filter((value) => !selectedIds.includes(value.referencedFacetValueId))
      .map((value) => ({ ...value, checked: false })) ?? [],
  );

  const selectedValues = $derived(selectedWithCategories.filter((value) => value.checked));
  const unselectedValues = $derived(unselectedWithoutSelectedCategories.filter((value) => !value.checked) ?? []);

  const unselectedNbaValues = $derived(
    unselectedWithoutSelectedCategories.filter(isNba).filter((value) => !value.checked) ?? [],
  );

  /*                             */
  const unselectedNonNbaValues = $derived(
    unselectedWithoutSelectedCategories.filter(isNotNba).filter((value) => !value.checked) ?? [],
  );

  const hasSelectedValues = $derived(nbaValues.find((value: FacetValue) => value.checked));

  const e3792Variation = getActiveVariation("e3792");
  const isE3792ContainsFacetValues = e3792Variation === "FacetValue" || e3792Variation === "ValueFacet";

  function limitNbaValuesByFacet(values: FacetValue[], selectedValues: FacetValue[]): FacetValue[] {
    /*                                                       */
    const facets = new Map<string, FacetValue[]>();
    for (const facetValue of values) {
      const facetName = facetValue.name ?? "";
      if (!facets.has(facetName)) facets.set(facetName, []);
      facets.get(facetName)!.push(facetValue);
    }
    const facetList = Array.from(facets.values());

    let visible: FacetValue[] = [];

    if (facetList.length >= 2) {
      const keys = Array.from(facets.keys());

      const firstFacetName = keys[0];
      const secondFacetName = keys[1];
      const selectedValuesOfFirstFacet = selectedValues.filter(
        (value) => value.name === firstFacetName && getInitialNbaChipIds().has(value.id),
      ).length;

      const selectedValuesOfSecondFacet = selectedValues.filter(
        (value) => value.name === secondFacetName && getInitialNbaChipIds().has(value.id),
      ).length;

      visible.push(
        ...facetList[0].slice(0, 3 - selectedValuesOfFirstFacet),
        ...facetList[1].slice(0, 2 - selectedValuesOfSecondFacet),
      );
    } else if (facetList.length === 1) {
      visible.push(...facetList[0].slice(0, 5));
    } else {
      visible.push(...values);
    }
    return visible;
  }

  const visibleNbaValues = $derived(
    isE3792ContainsFacetValues ? limitNbaValuesByFacet(unselectedNbaValues, selectedValues) : unselectedNbaValues,
  );

  const visibleNbaCount = $derived(visibleNbaValues.length);

  function slugifyTitle(text: string): string {
    if (text.includes(">")) return text;
    return text
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/\/*     */
      .replace(/\s+/g, "-");
  }

  /*                                                                         */
  /*                                                               */
  const { initialSelected, initialVisible } = (() => {
    if (!isE3792ContainsFacetValues) return { initialSelected: [], initialVisible: [] };
    const selected = nbaValues.filter((v) => v.nba && v.checked);
    const unselected = nbaValues.filter((v) => v.nba && !v.checked);
    const visible = limitNbaValuesByFacet(unselected, selected);
    setInitialNbaChipIds([...selected, ...visible].map((v) => v.id));
    return { initialSelected: selected, initialVisible: visible };
  })();

  function buildNbaTrackingString(key: "title" | "value"): string {
    if (!isE3792ContainsFacetValues) return topQuickFacet;
    return (
      [
        ...initialSelected.map((v) => slugifyTitle(v[key] ?? "")),
        ...initialVisible.map((v) => slugifyTitle(v[key] ?? "")),
        ...nextBestFilters.map((f) => f.id),
      ]
        .filter(Boolean)
        .join("|") || "none"
    );
  }

  const effectiveTopQuickFacet = getOrSetInitialTopQuickFacet(buildNbaTrackingString("title"));
  const effectiveNbaVisibleValues = getOrSetInitialNbaVisibleValues(buildNbaTrackingString("value"));

  /*                                                                           */
  /*        */
  /*                                                                           */

  function onchange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        if (event.target instanceof HTMLInputElement && event.target.checked === false) {
          filterForm.dataset.tsFeatureFilterMethod = "x_single_top";
        } else {
          filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
        }
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  /*                                                                          */
  /*                                                                                        */
  let nbaElement: HTMLDivElement | undefined = $state();
  let isOcMounted = false;

  $effect(() => {
    if (isNotActive("HEUREKA_1975_OC_CHIP_MIGRATION")) return;
    if (!nbaElement) return;

    const handler = (event: Event) => {
      if (isOcMounted) return;
      /*                                           */
      if (!(event.target instanceof HTMLElement) || !event.target.tagName.toLowerCase().startsWith("oc-chip")) return;
      isOcMounted = true;
      heureka.nextBestAction_loaded.emit();
    };

    nbaElement.addEventListener("oc-mount", handler);
    return () => nbaElement?.removeEventListener("oc-mount", handler);
  });

  function onClickDeselectAll() {
    selectedValues.forEach((value) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_FilterMethod", "x_all_top");
          updateLabel(referenced, "san_FacetLocation", "list_initial");
          updateStatus(referenced, "deleted", 2);
        }
      }
    });
  }

  function onSubmitDeselectAll(event: SubmitEvent) {
    emptyCategoryPathStore();
    selectedValues.forEach((value) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById<HTMLInputElement>(value.referencedFacetValueId);
        if (referenced) {
          clearFormInput(referenced);
        }
      }
      interceptSubmit(event);
    });
  }

  function openNextBestFilter(filterId: string) {
    eventQBus.emit("ftfind.filterSheet.openNextBestFilterDetail", { filterId: filterId });
  }
</script>

{#snippet closeIcon()}
  <oc-icon-v1 class="hc_facetValue__icon" type="close" size="50"></oc-icon-v1>
{/snippet}

{#snippet chip(facetValue: FacetValue)}
  {#if isActive("HEUREKA_1975_OC_CHIP_MIGRATION")}
    <Chip class={[{ js_clearInput: facetValue.checked }]} {...facetValue} showRemoveIcon={!!facetValue.checked}>
      <Text {...facetValue} {...defaultFacetValueLabelOptions} />
    </Chip>
  {:else}
    <ChipDeprecated class={[{ js_clearInput: facetValue.checked }]} {...facetValue}>
      <Text {...facetValue} {...defaultFacetValueLabelOptions} />
      {#if facetValue.checked}
        {@render closeIcon()}
      {/if}
    </ChipDeprecated>
  {/if}
{/snippet}

{#snippet nextBestFilterChips(nextBestFilters: NextBestFilter[])}
  <div class="hc_filterGroup__container">
    <ul class="hc_filterGroup__list">
      {#each nextBestFilters as nextBestFilter (nextBestFilter.title)}
        <li class="hc_filterGroup__item">
          <ChipAction
            ref="find_filter_{nextBestFilter.id}"
            title={nextBestFilter.title}
            selected={nextBestFilter.selected}
            itemCount={nextBestFilter.itemCount}
            onclick={() => openNextBestFilter(nextBestFilter.id)}
          ></ChipAction>
        </li>
      {/each}
    </ul>
  </div>
{/snippet}

<div
  bind:this={nbaElement}
  class="hc_nextBestAction{hasSelectedValues ? '' : ' hc_nextBestAction--unselected'}"
  hidden={!hasValues}
  data-ts-top-quick-facet={effectiveTopQuickFacet}
  data-ts-nba-visible-values={effectiveNbaVisibleValues}
  data-ts-nba-facet-value-count={visibleNbaCount}
  data-ts-labels={JSON.stringify({
    san_NBAFacetValueCount: visibleNbaCount,
  })}
  {onchange}
>
  {#if hasValues}
    <h4>
      {#if containsOnlyNbaValues && !(selectedValues.length > 0)}Beliebte Filter{:else}Gewählte und beliebte Filter{/if}
    </h4>
    <div class="hc_nextBestAction__content">
      <div class="hc_nextBestAction__values">
        {#if hasNextBestFilters}
          <OverflowBox values={selectedValues} item={chip}></OverflowBox>
          {#if hasSelectedValues}
            <oc-divider-v1 orientation="vertical"></oc-divider-v1>
          {/if}
          {#if !showNextBestFiltersFirst}
            <OverflowBox values={unselectedNonNbaValues} item={chip}></OverflowBox>
          {/if}
          {#if showNextBestFiltersFirst}
            {#if nextBestFilters.length > 0}
              {@render nextBestFilterChips(nextBestFilters)}
            {/if}
            <OverflowBox values={unselectedNonNbaValues} item={chip}></OverflowBox>
          {/if}
          <OverflowBox values={visibleNbaValues} item={chip}></OverflowBox>
          {#if !showNextBestFiltersFirst && nextBestFilters.length > 0}
            {@render nextBestFilterChips(nextBestFilters)}
          {/if}
        {:else}
          <OverflowBox values={selectedValues} item={chip}></OverflowBox>
          <OverflowBox values={unselectedValues} item={chip}></OverflowBox>
        {/if}
      </div>
      {#if hasSelectedValues}
        <div
          class="hc_nextBestAction__deselectAll"
          class:hc_facetValueTags__deselectAll--hide-desktop={hideDeselectAllOnDesktopView}
        >
          <DeselectAll {...deselectAll} onclick={onClickDeselectAll} onsubmit={onSubmitDeselectAll} />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_nextBestAction {
    margin-bottom: tokens.$oc-semantic-spacing-50;

    &--unselected {
      padding-bottom: tokens.$oc-semantic-spacing-50;
    }

    h4 {
      padding: tokens.$oc-semantic-spacing-50 0 tokens.$oc-semantic-spacing-50;
      font: tokens.$oc-semantic-font-copy-75;
      color: tokens.$oc-semantic-color-text-secondary;
    }

    ul {
      display: flex;
      gap: tokens.$oc-semantic-spacing-50;
      align-items: center;
    }

    &__content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px 0;
    }

    &__values {
      display: flex;
      /*              */
      gap: tokens.$oc-semantic-spacing-50;
      flex: 1 1 auto;
    }

    &__values {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;

      .hc_facetValue__icon {
        width: tokens.$oc-semantic-spacing-75;
        height: tokens.$oc-semantic-spacing-75;
        margin-left: tokens.$oc-semantic-spacing-25;
      }
    }

    &__deselectAll {
      flex: 1;
      white-space: nowrap;
      text-align: right;
      padding-left: tokens.$oc-semantic-spacing-75;
      padding-right: tokens.$oc-semantic-spacing-25;
    }
  }
</style>
